/**
 * PoW 求解器 —— 优先 Web Worker, 不可用时退回主线程分片。
 *
 * ## 为什么必须离开主线程(2026-08-18 线上事故)
 *
 * 原实现只有主线程 `setTimeout(step, 0)` 分片一条路。它在桌面端一秒内出解, 但在手机上会卡死:
 *  - `setTimeout(_, 0)` 被规范夹到 4ms 起步, 嵌套更久; 页面失焦/滚动时 WebView 直接节流到秒级;
 *  - both 模式下用户正在拖滑块, 触摸事件与 canvas 重绘和求解抢同一个主线程。
 *
 * 结果是滑块两三秒就过了、PoW 迟迟不出解, 而 both 模式要两者都有才提交 —— 用户对着一个写着
 * "验证通过"的滑块反复点登录, 什么都不会发生, 服务端连一条日志都没有(压根没走到登录 action)。
 * 5ff 线上实测: 手机端要试 2-3 次才登得进去, 桌面端 0 复现。
 *
 * Worker 里没有 UI 事件、不被主线程节流波及, 求解与拖滑块真正并行, 这才是根治。
 *
 * ## Worker 源码怎么来的
 *
 * 由 `createSha256.toString()` 派生(见 ../sha256 里那段注释): 全站只有一份 sha256 真源,
 * 不存在"Worker 里一份、主线程一份"的手抄副本漂移。代价是 sha256 实现必须保持自包含闭包。
 *
 * ## 兜底
 *
 * 老 WebView 没有 Worker/Blob、CSP 禁 blob: worker、Worker 内抛错 —— 任一情况都**静默回退**
 * 主线程分片, 行为与改造前完全一致。宁可慢, 不可不能用。
 */
import { createSha256 } from "../sha256.js";
/** 主线程分片每片跑多少次 hash。仅 fallback 用 —— 太大掉帧, 太小被 4ms clamp 拖慢。 */
const MAIN_CHUNK = 2000;
/** Worker 每算这么多次回报一次进度。取 2 的幂, 用位与判断。 */
const WORKER_PROGRESS_MASK = 2047;
let workerSourceCache = null;
/** 拼 Worker 源码。只在第一次用到时拼, 之后走缓存。 */
function workerSource() {
    if (workerSourceCache)
        return workerSourceCache;
    workerSourceCache = [
        `var createSha256=${createSha256.toString()};`,
        `var sha256hex=createSha256();`,
        `self.onmessage=function(e){`,
        `  var d=e.data,salt=d.salt,challenge=d.challenge,max=d.maxnumber;`,
        `  for(var n=0;n<=max;n++){`,
        `    if(sha256hex(salt+n)===challenge){self.postMessage({t:"done",n:n});return;}`,
        `    if((n&${WORKER_PROGRESS_MASK})===0)self.postMessage({t:"p",v:n/(max+1)});`,
        `  }`,
        `  self.postMessage({t:"fail"});`,
        `};`,
    ].join("\n");
    return workerSourceCache;
}
/** 主线程分片求解(改造前的老路径, 现在只作 fallback)。 */
function solveOnMainThread(task, cb) {
    let cancelled = false;
    let n = 0;
    const sha256hex = createSha256();
    const step = () => {
        if (cancelled)
            return;
        const end = Math.min(n + MAIN_CHUNK, task.maxnumber + 1);
        for (; n < end; n++) {
            if (sha256hex(task.salt + n) === task.challenge) {
                cb.onDone(n);
                return;
            }
        }
        if (n <= task.maxnumber) {
            cb.onProgress?.(n / (task.maxnumber + 1));
            setTimeout(step, 0);
        }
        else {
            cb.onFail();
        }
    };
    setTimeout(step, 0);
    return {
        mode: "main",
        cancel: () => {
            cancelled = true;
        },
    };
}
/** 环境是否具备起 Worker 的条件。只做能力探测, 真失败仍由下面的 try/catch + onerror 兜。 */
function canUseWorker() {
    return (typeof Worker !== "undefined" &&
        typeof Blob !== "undefined" &&
        typeof URL !== "undefined" &&
        typeof URL.createObjectURL === "function");
}
/**
 * 求解 PoW。返回 handle, 组件卸载时务必 `cancel()`。
 * 任何 Worker 侧的问题都会静默回退主线程, 调用方不用关心走的哪条路。
 */
export function solvePow(task, cb) {
    if (!canUseWorker())
        return solveOnMainThread(task, cb);
    let url = null;
    let worker = null;
    /** 已经交付过结果(done/fail/回退)就不再让任何后续回调生效 */
    let settled = false;
    /** 回退用: Worker 出错时若尚未出解, 换主线程重跑 */
    let fallback = null;
    const dispose = () => {
        if (worker) {
            worker.terminate();
            worker = null;
        }
        if (url) {
            URL.revokeObjectURL(url);
            url = null;
        }
    };
    try {
        url = URL.createObjectURL(new Blob([workerSource()], { type: "text/javascript" }));
        worker = new Worker(url);
    }
    catch {
        dispose();
        return solveOnMainThread(task, cb);
    }
    worker.onmessage = (e) => {
        if (settled)
            return;
        const d = e.data;
        if (d.t === "p") {
            cb.onProgress?.(d.v ?? 0);
        }
        else if (d.t === "done") {
            settled = true;
            dispose();
            cb.onDone(d.n);
        }
        else if (d.t === "fail") {
            settled = true;
            dispose();
            cb.onFail();
        }
    };
    // Worker 起不来或内部抛错(旧 WebView 语法不支持、CSP 拦 blob: 等) → 回退主线程, 别把用户卡死
    worker.onerror = () => {
        if (settled)
            return;
        settled = true;
        dispose();
        fallback = solveOnMainThread(task, cb);
    };
    worker.postMessage(task);
    return {
        mode: "worker",
        cancel: () => {
            settled = true;
            dispose();
            fallback?.cancel();
        },
    };
}
